"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { CIDADES, UFS, VIEWBOX, type Cidade } from "./brasil-geo";
import { WHATSAPP_URL } from "@/lib/site";

/* Simulação de vendas em tempo real calibrada pelo volume público da
   plataforma (R$ 900 mi/ano ≈ R$ 2,46 mi/dia). Cidades reais, com peso
   extra para capitais e polos de moda/calçado. */

const SEGMENTOS = [
  "Moda feminina",
  "Calçados",
  "Moda masculina",
  "Moda infantil",
  "Fitness",
  "Acessórios",
  "Atacado B2B",
];

const PESO: Record<string, number> = {
  "São Paulo": 6, "Rio de Janeiro": 4, "Belo Horizonte": 3, "Curitiba": 3,
  "Porto Alegre": 3, "Salvador": 2, "Recife": 2, "Fortaleza": 2, "Goiânia": 2,
  "Novo Hamburgo": 2, "Birigui": 2, "Franca": 2, "Jaú": 2, "Ribeirão Preto": 2,
};
const POOL: Cidade[] = CIDADES.flatMap((c) => Array(PESO[c.nome] ?? 1).fill(c));

const GMV_DIA = 2_465_000; // R$ 900 mi / 365

type Venda = { id: number; cidade: Cidade; valor: number; segmento: string };

const fmt = (v: number) =>
  v.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });

function novaVenda(id: number): Venda {
  const cidade = POOL[Math.floor(Math.random() * POOL.length)];
  // ticket: maioria 120–420, cauda até ~2.800 (kits/atacado)
  const r = Math.random();
  const valor =
    r < 0.7 ? 120 + Math.random() * 300 : r < 0.93 ? 420 + Math.random() * 700 : 1100 + Math.random() * 1700;
  return {
    id,
    cidade,
    valor: Math.round(valor / 10) * 10,
    segmento: SEGMENTOS[Math.floor(Math.random() * SEGMENTOS.length)],
  };
}

export default function MapaVivo() {
  const fraseRef = useRef<HTMLSpanElement>(null);
  const [feed, setFeed] = useState<Venda[]>([]);
  const [ripples, setRipples] = useState<Venda[]>([]);
  const [desdeAbertura, setDesdeAbertura] = useState(0);
  const [gmv, setGmv] = useState(0);
  const [ultimaHora, setUltimaHora] = useState(0);
  const idRef = useRef(1);

  // âncora: fração do dia já decorrida sobre o GMV médio diário
  useEffect(() => {
    const agora = new Date();
    const fracaoDia = (agora.getHours() * 3600 + agora.getMinutes() * 60 + agora.getSeconds()) / 86400;
    setGmv(Math.round(GMV_DIA * fracaoDia));
    setUltimaHora(560 + Math.floor(Math.random() * 90));
  }, []);

  useEffect(() => {
    let vivo = true;
    let timer: ReturnType<typeof setTimeout>;
    const tick = () => {
      if (!vivo) return;
      const v = novaVenda(idRef.current++);
      setFeed((f) => [v, ...f].slice(0, 3));
      setRipples((r) => [...r.slice(-5), v]);
      setDesdeAbertura((n) => n + 1);
      setGmv((g) => g + v.valor);
      setUltimaHora((u) => u + (Math.random() < 0.5 ? 1 : 0));
      timer = setTimeout(tick, 1300 + Math.random() * 1600);
    };
    timer = setTimeout(tick, 600);
    return () => {
      vivo = false;
      clearTimeout(timer);
    };
  }, []);

  const atual = feed[0];
  const cidadeAtual = atual?.cidade.nome;
  useEffect(() => {
    if (!cidadeAtual) return;
    fraseRef.current?.animate(
      [
        { opacity: 0, transform: "translateY(35%)" },
        { opacity: 1, transform: "none" },
      ],
      { duration: 650, easing: "cubic-bezier(0.22, 1, 0.36, 1)" }
    );
  }, [cidadeAtual]);

  // câmera: zoom deslizando até a cidade do último pedido (o mapa
  // flutua sobre o fundo escuro, então centralizar de verdade é seguro)
  const CAM_S = 1.35;
  const VB_W = 1000, VB_H = 1027;
  let camera = "translate(0px, 0px) scale(1)";
  if (atual) {
    camera = `translate(${(VB_W / 2 - CAM_S * atual.cidade.x).toFixed(1)}px, ${(VB_H / 2 - CAM_S * atual.cidade.y).toFixed(1)}px) scale(${CAM_S})`;
  }

  return (
    <section className="relative -mt-16 overflow-hidden bg-ink text-paper lg:-mt-20 lg:flex lg:h-svh lg:min-h-[680px] lg:flex-col lg:justify-center">
      <div className="glow-lime absolute inset-0" />

      <div className="relative mx-auto grid w-full max-w-7xl gap-10 px-5 pb-16 pt-32 lg:grid-cols-[0.9fr_1.1fr] lg:items-center lg:gap-6 lg:px-10 lg:pb-4 lg:pt-24">
        {/* coluna de texto */}
        <div className="relative z-10">
          <p className="inline-flex items-center gap-2.5 rounded-full border border-lime/40 bg-lime/10 px-4 py-2">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-lime opacity-70" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-lime" />
            </span>
            <span className="text-[12px] font-bold uppercase tracking-[0.18em] text-lime">
              Vendas ao vivo
            </span>
          </p>

          <h1 className="mt-5 text-[clamp(2.2rem,3.4vw,3.15rem)] font-bold leading-[1.0]">
            <span className="grid">
              {/* medidor invisível: reserva a altura da frase mais longa
                  para a troca de cidade não empurrar a página */}
              <span
                aria-hidden
                data-t="Feira de Santana está comprando neste exato momento."
                className="invisible col-start-1 row-start-1 before:content-[attr(data-t)]"
              />
              {/* o mesmo elemento em todas as trocas de cidade: remontar a frase
                  criaria um novo candidato a LCP a cada pedido (LCP de 9s+) */}
              <span ref={fraseRef} className="col-start-1 row-start-1 self-start">
                <span className="text-lime">{atual ? atual.cidade.nome : "O Brasil"}</span> está
                comprando neste exato momento.
              </span>
            </span>
          </h1>
          <p className="mt-4 max-w-md text-[17px] leading-relaxed text-paper/65">
            Cada pulso no mapa é um pedido acontecendo agora em uma loja que roda na
            Shoppub.
          </p>

          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="group mt-6 inline-flex items-center gap-3 rounded-full bg-lime py-2 pl-7 pr-2 text-base font-bold text-ink transition hover:bg-lime-deep"
          >
            Colocar minha marca no mapa
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-ink text-lime transition-transform duration-300 group-hover:rotate-45">
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <path d="M7 17L17 7M9 7h8v8" />
              </svg>
            </span>
          </a>

          {/* contadores */}
          <div className="mt-7 grid max-w-md grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="rounded-card border border-white/10 bg-ink-soft/80 p-4 backdrop-blur">
              <div className="text-[11px] font-bold uppercase tracking-wider text-smoke-dark">
                Vendas na última hora
              </div>
              <div className="mt-1 font-display text-2xl font-bold text-lime">
                {ultimaHora.toLocaleString("pt-BR")}
              </div>
              <div className="mt-0.5 text-[12px] text-smoke-dark">
                pedidos processados na plataforma
              </div>
            </div>
            <div className="rounded-card border border-white/10 bg-ink-soft/80 p-4 backdrop-blur">
              <div className="text-[11px] font-bold uppercase tracking-wider text-smoke-dark">
                GMV hoje
              </div>
              <div className="mt-1 font-display text-2xl font-bold text-lime">{fmt(gmv)}</div>
              <div className="mt-0.5 text-[12px] text-smoke-dark">
                vendidos pelas marcas Shoppub
              </div>
            </div>
          </div>

          <p className="mt-4 max-w-md text-[11px] leading-relaxed text-paper/35">
            Visualização em tempo real calibrada pelo volume da plataforma — R$ 900 milhões
            por ano em GMV, mais de 500 marcas premium.
          </p>
        </div>

        {/* mapa */}
        <div className="relative">
          <svg viewBox={VIEWBOX} className="mx-auto w-full lg:h-[calc(100svh-170px)] lg:max-h-[820px] lg:w-auto lg:max-w-full" role="img" aria-label="Mapa do Brasil com vendas em tempo real">
            <defs>
              <filter id="pulso-glow" x="-200%" y="-200%" width="500%" height="500%">
                <feGaussianBlur stdDeviation="7" />
              </filter>
            </defs>
            <g
              style={{
                transform: camera,
                transition: "transform 1.7s cubic-bezier(0.22, 1, 0.36, 1)",
              }}
            >
            <g>
              {UFS.map((d, i) => (
                <path key={i} d={d} fill="#141419" stroke="rgba(255,255,255,0.09)" strokeWidth="1.2" />
              ))}
            </g>
            {/* pontos fixos das cidades */}
            {CIDADES.map((c) => (
              <circle key={c.nome} cx={c.x} cy={c.y} r="3" fill="rgba(255,255,255,0.22)" />
            ))}
            {/* pulsos de venda: halo + núcleo + anéis */}
            {ripples.map((v) => (
              <g key={v.id}>
                <circle cx={v.cidade.x} cy={v.cidade.y} r="14" fill="#c9f158" opacity="0.45" filter="url(#pulso-glow)">
                  <animate attributeName="opacity" values="0.45;0.25;0.45" dur="1.6s" repeatCount="indefinite" />
                </circle>
                <circle cx={v.cidade.x} cy={v.cidade.y} r="7" fill="#c9f158" />
                <circle cx={v.cidade.x} cy={v.cidade.y} r="2.6" fill="#fbffe8" />
                <circle cx={v.cidade.x} cy={v.cidade.y} r="7" fill="none" stroke="#c9f158" strokeWidth="2.6" opacity="0.9">
                  <animate attributeName="r" from="7" to="52" dur="2.6s" begin="0s" fill="freeze" />
                  <animate attributeName="opacity" from="0.9" to="0" dur="2.6s" begin="0s" fill="freeze" />
                </circle>
                <circle cx={v.cidade.x} cy={v.cidade.y} r="7" fill="none" stroke="#c9f158" strokeWidth="1.6" opacity="0.55">
                  <animate attributeName="r" from="7" to="92" dur="3.4s" begin="0.35s" fill="freeze" />
                  <animate attributeName="opacity" from="0.55" to="0" dur="3.4s" begin="0.35s" fill="freeze" />
                </circle>
              </g>
            ))}
            </g>
          </svg>

          {/* feed de pedidos */}
          <div className="pointer-events-none absolute bottom-2 right-0 flex w-full max-w-[330px] flex-col-reverse gap-2 sm:bottom-6">
            {feed.map((v, i) => (
              <div
                key={v.id}
                style={{ opacity: 1 - i * 0.3 }}
                className={`flex items-center gap-3 rounded-2xl border border-white/12 bg-ink-soft/90 p-3 shadow-[0_16px_40px_-18px_rgba(0,0,0,0.8)] backdrop-blur transition-all duration-500 ${
                  i === 0 ? "animate-[reveal-up_0.55s_cubic-bezier(0.22,1,0.36,1)]" : ""
                }`}
              >
                <span className="h-9 w-9 shrink-0 overflow-hidden rounded-xl">
                  <Image src="/brand/favicon_preto.svg" alt="" width={36} height={36} className="h-full w-full" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13px] font-bold text-paper">
                    {v.segmento}
                  </span>
                  <span className="block text-[11px] text-smoke-dark">
                    {v.cidade.nome} · {v.cidade.uf} · agora
                  </span>
                </span>
                <span className="shrink-0 text-[14px] font-bold text-lime">{fmt(v.valor)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
